import clsx from 'clsx'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'

import FlashHotelDesktopSameRoomOptions from './FlashHotelDesktopSameRoomOptions'

import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryDown } from 'components/utils/breakpoint'
import config from 'constants/config'
import useIsFlightsDisabledForGeoState from 'hooks/Flights/useIsFlightsDisabledForGeoState'
import type { OfferPageFilterType, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useReturnForFreeSaleAvailability from 'hooks/CharterFlights/useReturnForFreeSaleAvailability'
import MaldivesOfferReturnForFreeBanner from 'components/OfferPage/CharterBundle/MaldivesOfferReturnForFreeBanner'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import type { OfferDurationOption } from 'lib/offer/offerDurationTypes'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import FlashHotelDesktopIndividualRooms from './FlashHotelDesktopIndividualRooms'
import WalledContent from 'components/Common/WalledContent'
import {
  getCalendarDurationOptionsWithCharterBundleRestrictions,
  getCanUseMultiRoomUI, getCheckoutDate, getCheapestSelectedPackageOptionWithDefault,
  getEffectiveCharterBundleRoute, getHasSoloCapacityPackages, isAvailableForPurchase,
  offerFlightsEnabled,
} from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import HotelDesktopFlightToggle from '../Accommodation/HotelDesktopFlightToggle'
import MultiRoomModeToggle from '../MultipleRoomsBooking/MultiRoomModeToggle'
import OfferDesktopConnectingRoomsToggle from '../Filters/ConnectingRooms/OfferDesktopConnectingRoomsToggle'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { NON_CHARTER_TO_CHARTER_OFFER_MAP } from 'constants/charterFlight'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import CharterBundleBanner from '../CharterBundle/CharterBundleBanner'
import ReturnCharterFlightTile from 'components/Flights/CharterBundle/ReturnCharterFlightTile'
import CharterFlightOfferAirportSelect from '../Filters/CharterFlight/CharterFlightOfferAirportSelect'
import CharterFlightFareClassSelect from '../Filters/CharterFlight/CharterFlightFareClassSelect'
import CharterFlightCalendarSelect from '../Filters/CharterFlight/CharterFlightCalendarSelect'
import { toBaseFareClass, type FlightsClassTypes } from 'constants/flight'
import Caption from 'LuxKit/Typography/Caption'
import moment from 'moment'
import { ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import PriceModeSelectDesktop from 'components/OfferPage/Filters/PriceModeSelect/PriceModeSelectDesktop'
import Switch from 'LuxKit/Switch/Switch'
import { OfferFilterSelectButton, OfferFilterSelectButtonContent } from 'components/OfferPage/Filters/OfferFilterSelectButton'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import PageBanner from 'LuxKit/Banners/PageBanner'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import NestableClickable from 'components/Common/NestableClickable'
import type { DateRange } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/types'
import { FormattedMessage, useIntl } from 'react-intl'
import OfferPageLoyaltyPointsSlider from 'components/OfferPage/Loyalty/OfferPageLoyaltyPointsSlider'
import TravellingSoloBanner, { EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT, EXPANDED_EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT } from 'components/Common/TravellingSoloBanner/TravellingSoloBanner'
import { isSoloOccupancy } from 'lib/offer/occupancyUtils'
import FlashHotelValueComparison from './FlashHotelValueComparison'
import { getMultiRoomPackageTotals, type MultiRoomPackageTotals } from 'selectors/offerPage/multipleRoomBookingSelectors'
import getFlashHotelValueComparisonPricing from './getFlashHotelValueComparisonPricing'

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledOfferCapacityWithAgesSelect = styled(OfferCapacityWithAgesSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferCalendarDatePickerSelect = styled(OfferCalendarDatePickerSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;

  &.has-top-banner {
    margin-top: ${rem(EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT)};
  }

  &.has-expanded-top-banner {
    margin-top: ${rem(EXPANDED_EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT)};
  }

  &:has(> :nth-child(3)) > * {
    flex: 1;
    max-width: unset;
  }

  ${mediaQueryDown.desktop} {
    &:has(> :nth-child(4)) > * {
      /* 4 filters (charter bundle: guests / airport / class / when) wrap into a 2x2 grid with consistent column widths across rows. */
      flex: 1 1 calc(50% - ${rem(4)});
      max-width: calc(50% - ${rem(4)});
      min-width: 34%;
    }
  }
`

const SpacedFlashHotelValueComparison = styled(FlashHotelValueComparison)`
  // Extra spacing to handle position absolute SelectBoxTopBanner component
  margin-bottom: ${rem(8)};
`

interface Props {
  offer: App.HotelOffer
  airport?: App.Airport
  duration: number
  activeFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  availableForPurchase?: boolean
  flightsEnabled?: boolean
  multipleRoomBookingActive: boolean
  canUseMultiRoomUI: boolean
  charterBundleRoute?: App.CharterBundleRoute
  charterBundleEnabled: boolean
  fareClass: FlightsClassTypes
  effectiveCharterBundleRoute?: App.CharterBundleRoute
  calendarDurationOptions: Array<OfferDurationOption>
  isSpoofed: boolean
  setIsStickyFooterVisible: (isVisible: boolean) => void
  showFlightInformation: boolean
  charterDateRangesByMonth: Record<string, Array<DateRange>>
  cheapestCharterDateRange?: DateRange
  onCharterDurationChange: (nights: number) => void
  occupancyLimits: Array<App.RoomCapacity>
  destinationAirport?: App.Airport
  desktopFilterBlockRef: React.RefObject<HTMLDivElement | null>
  hasSoloCapacityPackages: boolean
  cheapestOption: App.OfferPackageOptionView
  multiRoomPackageTotals?: MultiRoomPackageTotals
  priceMode: OfferPagePriceMode
}

function FlashHotelDesktopRoomOptions(props: Props) {
  const {
    offer,
    airport,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    availableForPurchase,
    flightsEnabled,
    multipleRoomBookingActive,
    canUseMultiRoomUI,
    charterBundleRoute,
    charterBundleEnabled,
    fareClass,
    effectiveCharterBundleRoute,
    calendarDurationOptions,
    isSpoofed,
    setIsStickyFooterVisible,
    showFlightInformation,
    charterDateRangesByMonth,
    cheapestCharterDateRange,
    onCharterDurationChange,
    occupancyLimits,
    destinationAirport,
    desktopFilterBlockRef,
    hasSoloCapacityPackages,
    cheapestOption,
    multiRoomPackageTotals,
    priceMode,
  } = props

  const intl = useIntl()

  const isFlightsDisabledForGeoState = useIsFlightsDisabledForGeoState()
  const { charterBundleDurationOptions } = useCharterFlightLabels()
  const isHotelOnlyModeOnCharterOffer = !!charterBundleRoute && !charterBundleEnabled
  const showSoloBanner = hasSoloCapacityPackages && !isSoloOccupancy(rooms)
  const showConnectingRoomsToggle = rooms.length > 1 && !effectiveCharterBundleRoute

  const stateDispatch = useOfferPageDispatch()
  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  // Charter calendar UI state
  const [selectedMonth, setSelectedMonth] = useState<string>(
    checkInDate ? moment(checkInDate).format(ISO_DATE_SHORT_FORMAT) : moment().format(ISO_DATE_SHORT_FORMAT),
  )

  useEffect(() => {
    if (!checkInDate && cheapestCharterDateRange?.checkIn) {
      setSelectedMonth(moment(cheapestCharterDateRange.checkIn).format(ISO_DATE_SHORT_FORMAT))
    }
  }, [checkInDate, cheapestCharterDateRange?.checkIn])

  useEffect(() => {
    const months = Object.keys(charterDateRangesByMonth)
    if (months.length > 0 && !months.includes(selectedMonth)) {
      setSelectedMonth(months[0])
    }
  }, [charterDateRangesByMonth, selectedMonth])

  const flightTileCheckIn = checkInDate || cheapestCharterDateRange?.checkIn
  const flightTileCheckOut = checkOutDate || cheapestCharterDateRange?.checkOut
  const valueComparisonPricing = useMemo(
    () => getFlashHotelValueComparisonPricing({
      packageOption: cheapestOption,
      priceMode,
      rooms,
      multiRoomPackageTotals,
    }),
    [cheapestOption, multiRoomPackageTotals, priceMode, rooms],
  )

  const { isSaleVisible: isReturnForFreeSaleVisible } = useReturnForFreeSaleAvailability()
  const showReturnForFreeBanner = isReturnForFreeSaleVisible &&
    !!effectiveCharterBundleRoute &&
    !checkInDate

  const handleCheckReturnForFreeAvailability = useCallback(() => {
    stateDispatch({ type: OfferPageActions.checkAvailability, filter: 'checkin', flights: true })
  }, [stateDispatch])

  const handleCharterBundleToggle = useCallback(() => {
    stateDispatch({
      type: OfferPageActions.setCharterBundleEnabled,
      charterBundleEnabled: !charterBundleEnabled,
    })
  }, [charterBundleEnabled, stateDispatch])

  const charterBundleToggleValue = charterBundleEnabled
    ? intl.formatMessage({
        id: 'ecxcPGUQ4s3e3LsDp7_v',
        defaultMessage: 'Included in package',
        description: 'Text indicating charter flights are included in package',
      })
    : intl.formatMessage({
        id: 'qJeuriRpfQ9hCS7gIT1T',
        defaultMessage: 'Hotel only',
        description: 'Text indicating hotel only option on the hotel or room booking page',
      })

  return (
    <VerticalSpacer gap={32}>
      <LayoutContainer>
        <VerticalSpacer gap={32}>
          <SpacedFlashHotelValueComparison
            price={valueComparisonPricing.price}
            value={valueComparisonPricing.value}
            discountPercent={valueComparisonPricing.discountPercent}
          />
          {availableForPurchase && <WalledContent enforced={offer.walledGarden}>
            <CSSBreakpoint min="tablet">
              <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
              <FlexDesktopFilterBlock
                ref={desktopFiltersWrapperRef}
                direction="horizontal"
                gap={8}
                horizontalAlign="center"
                wrap="wrap"
                className={clsx({
                  open: !!activeFilter,
                  'has-top-banner': showSoloBanner && !showConnectingRoomsToggle,
                  'has-expanded-top-banner': showSoloBanner && showConnectingRoomsToggle,
                })}
              >
                <StyledOfferCapacityWithAgesSelect
                  boundaryRef={desktopFiltersWrapperRef}
                  capacities={occupancyLimits}
                  disableMultiRoom={isFlightForceBundled(offer)}
                  topBanner={showSoloBanner ? <TravellingSoloBanner isExpanded={showConnectingRoomsToggle} /> : undefined}
                />
                {charterBundleRoute && isSpoofed && (
                  <OfferFilterSelectButton
                    as={NestableClickable}
                    onClick={handleCharterBundleToggle}
                    data-testid="charter-bundle-toggle"
                  >
                    <LinePlaneDepartureIcon />
                    <OfferFilterSelectButtonContent>
                      <BodyText variant="medium" wrap="truncate">
                        {intl.formatMessage({
                          id: 'lBRbQdjjmxipknXIfK_M',
                          defaultMessage: 'Charter flights',
                          description: 'Label for charter flights filter in the flight search or booking flow',
                        })}
                      </BodyText>
                      <Caption variant="medium" colour="light-foreground-alt" wrap="truncate">
                        {charterBundleToggleValue}
                      </Caption>
                    </OfferFilterSelectButtonContent>
                    <Switch
                      checked={charterBundleEnabled}
                      onChange={handleCharterBundleToggle}
                    />
                  </OfferFilterSelectButton>
                )}
                {effectiveCharterBundleRoute && <>
                  <CharterFlightOfferAirportSelect />
                  <CharterFlightFareClassSelect />
                  <div>
                    <CharterFlightCalendarSelect
                      selectedMonth={selectedMonth}
                      onMonthChange={setSelectedMonth}
                      nights={duration}
                      fareClass={fareClass}
                      onDurationChange={onCharterDurationChange}
                      durationOptions={charterBundleDurationOptions}
                      checkInDate={checkInDate}
                      charterBundleRoute={effectiveCharterBundleRoute}
                      rooms={rooms}
                      dateRangesByMonth={charterDateRangesByMonth}
                    />
                  </div>
                </>}
                {!effectiveCharterBundleRoute && <StyledOfferCalendarDatePickerSelect
                  boundaryRef={desktopFiltersWrapperRef}
                />}
                {showConnectingRoomsToggle && <OfferDesktopConnectingRoomsToggle />}
                {flightsEnabled && !effectiveCharterBundleRoute && !isFlightsDisabledForGeoState && <HotelDesktopFlightToggle />}
                <Backdrop visible={!!activeFilter} data-testid="filter-backdrop" />
              </FlexDesktopFilterBlock>
            </CSSBreakpoint>
          </WalledContent>}
          {effectiveCharterBundleRoute && <ReturnCharterFlightTile
            route={effectiveCharterBundleRoute}
            occupancies={rooms}
            checkInDate={flightTileCheckIn}
            checkOutDate={flightTileCheckOut}
            fareClass={fareClass}
            flightOrigin={airport}
          />}
          <VerticalSpacer gap={24}>
            {isHotelOnlyModeOnCharterOffer && (
              <PageBanner kind="warning" icon={<LineExclamationTriangleIcon />}>
                <FormattedMessage
                  id="5P2xH6VJn-AJTQVBunJe"
                  defaultMessage="This offer must be bundled with a Maldives Charter flight via Add To Cart. It cannot be purchased without an LE Charter flight.<br></br>Hotel-only bookings must be combined with another hotel stay as a 5+7, 6+6, or 7+5 nights combo."
                  description="Warning notice when hotel-only mode is selected on a charter bundle offer"
                  values={{
                    br: () => <br />,
                  }}
                />
              </PageBanner>
            )}
          </VerticalSpacer>
        </VerticalSpacer>
      </LayoutContainer>
      <OfferPageLoyaltyPointsSlider InnerWrapper={LayoutContainer} />
      <LayoutContainer>
        <VerticalSpacer gap={24}>
          {!effectiveCharterBundleRoute && <VerticalSpacer gap={16}>
            <VerticalSpacer gap={8}>
              <BodyText variant="large" align="center">
                {intl.formatMessage({
                  id: '1hY--ObMHBJZCdI2ht_M',
                  defaultMessage: 'Select duration',
                  description: 'Section heading for duration selection on the offer detail page',
                })}
              </BodyText>
              <OfferDurationTabs durationOptionsOverride={isHotelOnlyModeOnCharterOffer ? calendarDurationOptions : undefined} />
            </VerticalSpacer>
            {NON_CHARTER_TO_CHARTER_OFFER_MAP[offer.id] && (
              <CharterBundleBanner offer={offer} />
            )}
          </VerticalSpacer>}
          <VerticalSpacer gap={16}>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
              <div>
                <Heading variant="heading3" as="h2">
                  {intl.formatMessage(
                    {
                      id: 'VCDugTs-8l9la-tEywnD',
                      defaultMessage: 'Select your {count, plural, one {room} other {# rooms}}',
                      description: 'Heading for selecting room packages on the hotel or room booking page',
                    },
                    { count: rooms.length },
                  )}
                </Heading>
                {canUseMultiRoomUI && <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'fl1Ytt4-aiddKRGd_IO2',
                    defaultMessage: 'Choose the same package for all rooms, or customise each room individually.',
                    description: 'Description for multi-room booking options in the booking flow',
                  })}
                </BodyText>}
              </div>
              {!!charterBundleRoute && <PriceModeSelectDesktop />}
            </Group>
            {canUseMultiRoomUI && <Group direction="horizontal"><MultiRoomModeToggle inputsName="room-mode-desktop" /></Group>}
            {showReturnForFreeBanner && (
              <MaldivesOfferReturnForFreeBanner onCheckAvailability={handleCheckReturnForFreeAvailability} />
            )}
          </VerticalSpacer>
        </VerticalSpacer>
      </LayoutContainer>
      {!multipleRoomBookingActive && offer.packages.length > 0 && <FlashHotelDesktopSameRoomOptions
        destinationAirport={destinationAirport}
        onStickyFooterToggle={setIsStickyFooterVisible}
        showSoloBanner={showSoloBanner}
      />}
      {multipleRoomBookingActive && <FlashHotelDesktopIndividualRooms />}
      {showFlightInformation && (
        <LayoutContainer>
          <Caption variant="medium" colour="light-foreground-alt" align="center">
            {intl.formatMessage(
              {
                id: 'YDD1yHaoF63XoGSi-v0f',
                defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply.',
                description: 'Pricing disclaimer shown below hotel room options when flights are enabled',
              },
              { brandName: config.brandConfig.name },
            )}
          </Caption>
        </LayoutContainer>
      )}
    </VerticalSpacer>
  )
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'availableForPurchase'
  | 'flightsEnabled'
  | 'multipleRoomBookingActive'
  | 'canUseMultiRoomUI'
  | 'charterBundleRoute'
  | 'charterBundleEnabled'
  | 'fareClass'
  | 'effectiveCharterBundleRoute'
  | 'calendarDurationOptions'
  | 'isSpoofed'
  | 'hasSoloCapacityPackages'
  | 'cheapestOption'
  | 'multiRoomPackageTotals'
  | 'priceMode'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => {
  const multipleRoomBookingActive = isMultipleRoomBookingActive(state, appState)

  return {
    airport: state.airport,
    duration: state.duration,
    activeFilter: state.activeFilter,
    rooms: state.rooms,
    checkInDate: state.checkInDate,
    checkOutDate: getCheckoutDate(state),
    flightsEnabled: offerFlightsEnabled(state, appState),
    availableForPurchase: isAvailableForPurchase(state, appState),
    multipleRoomBookingActive,
    canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
    charterBundleRoute: state.charterBundleRoute,
    charterBundleEnabled: state.charterBundleEnabled,
    fareClass: toBaseFareClass(state.fareClass),
    effectiveCharterBundleRoute: getEffectiveCharterBundleRoute(state),
    calendarDurationOptions: getCalendarDurationOptionsWithCharterBundleRestrictions(state, appState),
    isSpoofed: appState.auth.account.isSpoofed,
    hasSoloCapacityPackages: getHasSoloCapacityPackages(state, appState),
    cheapestOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
    multiRoomPackageTotals: multipleRoomBookingActive ? getMultiRoomPackageTotals(state, appState) : undefined,
    priceMode: state.priceMode,
  }
}, FlashHotelDesktopRoomOptions)
